import React, { useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  HStack,
  VStack,
  Text,
  Button,
  Link,
  useToast,
} from "@chakra-ui/react";

import { useStores } from "../../store/store_context";
import ColumnManager from "../column_manager";
import ColumnValueFilter from "../column_value_filter";
import {
  HEADER_LINES,
  NO_FILTER_COLUMNS,
  NUMBER_COLUMNS,
  cellText,
  compare,
  formatDate,
  formatMoney,
  rowsPassing,
  sortKey,
} from "./overdue_utils";

// «Просроченная ДЗ (ПДЗ)» — те же столбцы и правила, что у инструмента в Б24:
// фильтр «⏷» по значениям у всех столбцов, кроме названия сделки; строка
// «Итого» наверху — число счетов в первой колонке и сумма ПДЗ, по строкам,
// прошедшим все фильтры.

const TableData = observer(({ data = [], renderButtons }) => {
  const { pageStore } = useStores();
  const toast = useToast();
  const [sortConfig, setSortConfig] = useState({ key: "OverdueDays", direction: "desc" });
  const [filters, setFilters] = useState({});

  // Новая выборка — прежние отборы по значениям к ней не относятся.
  const [dataRef, setDataRef] = useState(data);
  if (dataRef !== data) {
    setDataRef(data);
    setFilters({});
  }

  const availableColumns = useMemo(
    () => pageStore.columnConfigOverdue.filter((c) => c.active),
    [pageStore.columnConfigOverdue]
  );

  const shown = useMemo(() => rowsPassing(Array.isArray(data) ? data : [], filters), [data, filters]);

  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    const arr = [...shown];
    if (!key) return arr;
    arr.sort((a, b) => {
      const result = compare(sortKey(a, key), sortKey(b, key));
      return direction === "asc" ? result : -result;
    });
    return arr;
  }, [shown, sortConfig]);

  const totalDebt = useMemo(
    () => shown.reduce((sum, row) => sum + (Number(row?.OverdueRub) || 0), 0),
    [shown]
  );

  const handleSort = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);

  const renderCell = (col, row) => {
    const text = cellText(row, col.key);
    if (col.isLink) {
      const linkValue = col.linkField ? row?.[col.linkField] : row?.[col.key];
      if (text && linkValue != null && linkValue !== "") {
        return (
          <Link
            href={(col.linkTemplate || "").replace("{value}", String(linkValue))}
            isExternal
            // Светло-голубой: синий #3b82f6 плохо читался на чёрном фоне
            // (просьба заказчика 29.09.2026). Подчёркивание остаётся.
            color="#7dd3fc"
            textDecoration="underline"
            _hover={{ color: "#bae6fd" }}
            wordBreak="break-word"
          >
            {text}
          </Link>
        );
      }
    }
    return text;
  };

  const exportToExcel = useCallback(() => {
    try {
      const rows = sortedData.map((row) => {
        const o = {};
        availableColumns.forEach((c) => {
          o[c.title] = NUMBER_COLUMNS.has(c.key) ? Number(row?.[c.key]) || 0 : cellText(row, c.key);
        });
        return o;
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Просроченная ДЗ");
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([out], { type: "application/octet-stream" }), "overdue_receivables.xlsx");
    } catch (e) {
      toast({
        title: "Ошибка экспорта",
        description: String(e?.message || e),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [sortedData, availableColumns, toast]);

  const exportButton = (
    <Button size="sm" bg="#333" color="white" _hover={{ bg: "#444" }} onClick={exportToExcel} isDisabled={!sortedData.length}>
      Экспорт в Excel
    </Button>
  );

  const rowPad = `${(pageStore.rowHeight ?? 2) * 2}px`;
  const filtered = shown.length !== data.length;
  const numericAlign = (key) => (NUMBER_COLUMNS.has(key) || key === "IdDeal" ? "right" : "left");

  if (!data.length) return null;

  return (
    <VStack alignItems="flex-start" spacing={4} w="100%">
      <HStack textColor="white" gap="20px" flexWrap="wrap">
        <ColumnManager
          report="overdue"
          columns={pageStore.columnConfigOverdue}
          onChange={(newColumns) => pageStore.setColumnConfigOverdue(newColumns)}
        />
        {renderButtons ? renderButtons(exportButton) : exportButton}
        {Object.keys(filters).length > 0 && (
          <Button size="sm" variant="outline" color="white" borderColor="#555" _hover={{ bg: "#333" }} onClick={() => setFilters({})}>
            Сбросить фильтры столбцов
          </Button>
        )}
      </HStack>
      <Text fontSize="14px" color="gray.400">
        {filtered ? `Показано ${shown.length} из ${data.length} счетов` : `Счетов: ${data.length}`}
        {pageStore.overdue_as_of ? ` · просрочка на ${formatDate(pageStore.overdue_as_of)}` : ""}
      </Text>

      <Box w="100%" minH="100vh" h="100vh" overflow="scroll" borderRadius="md" borderWidth="1px" borderColor="gray.300">
        <Table variant="simple" size="md" color="white">
          <Thead background="#333" position="sticky" top={0} zIndex={10}>
            <Tr>
              {availableColumns.map((col) => {
                const arrow = sortConfig.key === col.key ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : "";
                const lines = HEADER_LINES[col.key];
                return (
                  <Th
                    key={col.key}
                    color="white"
                    cursor="pointer"
                    onClick={() => handleSort(col.key)}
                    w={col.width}
                    whiteSpace="nowrap"
                    _hover={{ bg: "#444" }}
                    bg="#333"
                  >
                    <HStack spacing={1} justify="space-between">
                      <Text as="span">
                        {lines
                          ? lines.map((line, i) => (
                              <React.Fragment key={line}>
                                {i > 0 && <br />}
                                {line}
                              </React.Fragment>
                            ))
                          : col.title}
                        {arrow}
                      </Text>
                      {!NO_FILTER_COLUMNS.has(col.key) && (
                        <ColumnValueFilter
                          columnKey={col.key}
                          title={col.title}
                          rows={data}
                          filters={filters}
                          onChange={setFilters}
                          textOf={cellText}
                          sortKeyOf={sortKey}
                        />
                      )}
                    </HStack>
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {/* «Итого» наверху, как в остальных отчётах: число счетов и сумма ПДЗ. */}
            <Tr background="#2a2a2a">
              {availableColumns.map((col, index) => (
                <Td key={`total-${col.key}`} fontWeight="bold" textAlign={numericAlign(col.key)} whiteSpace="nowrap" py={rowPad}>
                  {index === 0 ? `Итого ${shown.length}` : col.key === "OverdueRub" ? formatMoney(totalDebt) : ""}
                </Td>
              ))}
            </Tr>
            {sortedData.map((row, idx) => (
              <Tr key={`${row?.Num}-${idx}`} background={idx % 2 === 0 ? "rgba(26,26,26,1)" : "#333"}>
                {availableColumns.map((col) => (
                  <Td
                    key={`${row?.Num}-${idx}-${col.key}`}
                    py={rowPad}
                    maxWidth={col.width}
                    fontSize="md"
                    textAlign={numericAlign(col.key)}
                    whiteSpace={col.key === "DealTitle" || col.key === "Partner" ? "normal" : "nowrap"}
                    wordBreak="break-word"
                  >
                    {renderCell(col, row)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
});

export default TableData;
